// @ts-nocheck
/**
 * The story's UI language for the whole planner (vhp-planner docs/planner-skin.md, step 1): shared by the story
 * setup (assets/vhp-planner-story.js), the preview / paywall skin and the subscriber planner
 * (assets/vhp-planner-funnel.js). Theme-aware styles in assets/vhp-ui-story.css.
 *
 *   window.vhpUi.morph(el, change, { width, ms })   // a size change animated (FLIP): measure, change, glide
 *   window.vhpUi.growIn(el, { ms, to }) -> release  // a new element opens from 0 (gap and margin included)
 *   window.vhpUi.burst(el, { count, spread })       // confetti from an element (fixed layer, any screen)
 *   window.vhpUi.dm(el, lines, { face })             // Sören's DM thread (.vhp-dm__*), first bubble typed
 *   window.vhpUi.seen(key) / markSeen(key)           // "once" flags in localStorage (messages, intros)
 *   window.vhpUi.buzz(ms) · window.vhpUi.motionOk()
 */
(() => {
  const EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)';
  const CONFETTI = ['#5fd38d', '#fff', '#f2c14e', '#1a7a43', '#ff8a65'];
  const SEEN_KEY = 'vhp_ui_seen';

  const motionOk = () => !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const buzz = (ms) => {
    try {
      navigator.vibrate?.(ms);
    } catch (e) {
      /* not supported */
    }
  };

  const morph = (el, change, { width = true, ms = 340 } = {}) => {
    if (!el || !motionOk() || !el.offsetParent) {
      change();
      return;
    }
    const from = { w: el.offsetWidth, h: el.offsetHeight };
    change();
    const to = { w: el.offsetWidth, h: el.offsetHeight };
    if (from.w === to.w && from.h === to.h) return;
    el.style.transition = 'none';
    if (width) el.style.width = `${from.w}px`;
    el.style.height = `${from.h}px`;
    void el.offsetWidth;
    el.style.transition = `width ${ms}ms ${EASE}, height ${ms}ms ${EASE}`;
    if (width) el.style.width = `${to.w}px`;
    el.style.height = `${to.h}px`;
    window.setTimeout(() => {
      el.style.transition = '';
      el.style.width = '';
      el.style.height = '';
    }, ms + 40);
  };

  const growIn = (el, { ms = 320, to = 0 } = {}) => {
    const release = () => {
      if (!el) return;
      el.style.transition = '';
      el.style.height = '';
      el.style.marginTop = '';
      el.style.overflow = '';
    };
    if (!el || !motionOk()) return release;
    const h = to || el.offsetHeight;
    const gap = el.parentElement ? parseFloat(getComputedStyle(el.parentElement).rowGap) || 0 : 0;
    const margin = parseFloat(getComputedStyle(el).marginTop) || 0;
    el.style.overflow = 'hidden';
    el.style.height = '0px';
    el.style.marginTop = `${-gap}px`;
    void el.offsetWidth;
    el.style.transition = `height ${ms}ms ${EASE}, margin-top ${ms}ms ${EASE}`;
    el.style.height = `${h}px`;
    el.style.marginTop = `${margin}px`;
    if (!to) window.setTimeout(release, ms + 40);
    return release;
  };

  // Confetti on a fixed layer, so it works on any screen (the story has its own frame-bound one).
  const burst = (el, { count = 26, spread = 1 } = {}) => {
    if (!el || !motionOk()) return;
    const box = el.getBoundingClientRect();
    if (!box.width) return;
    const layer = document.createElement('div');
    layer.className = 'vhp-ui-burst';
    layer.setAttribute('aria-hidden', 'true');
    layer.style.left = `${box.left + box.width / 2}px`;
    layer.style.top = `${box.top + box.height / 2}px`;
    for (let i = 0; i < count; i += 1) {
      const bit = document.createElement('i');
      const angle = Math.random() * Math.PI * 2;
      const dist = (60 + Math.random() * 110) * spread;
      bit.style.background = CONFETTI[i % CONFETTI.length];
      bit.style.setProperty('--dx', `${Math.cos(angle) * dist}px`);
      bit.style.setProperty('--dy', `${Math.sin(angle) * dist - 40}px`);
      bit.style.setProperty('--r', `${Math.random() * 540 - 270}deg`);
      layer.append(bit);
    }
    document.body.append(layer);
    window.setTimeout(() => layer.remove(), 1300);
  };

  // Sören's DM: his face, then his lines as grouped bubbles, the first one typed (dots, then the words). Same
  // markup as the story's answer slide; timing and colours come from the CSS around it (.vhp-ui-dm in the app).
  const dm = (el, lines, { face = null } = {}) => {
    if (!el) return;
    const list = (lines || []).filter(Boolean);
    const key = list.join('\n');
    if (el.dataset.text === key) return;
    el.dataset.text = key;
    const them = document.createElement('div');
    them.className = 'vhp-dm__them';
    const pic = face?.cloneNode(true);
    if (pic) {
      pic.className = 'vhp-dm__face';
      them.append(pic);
    }
    const col = document.createElement('div');
    col.className = 'vhp-dm__col';
    list.forEach((line, n) => {
      const msg = document.createElement('p');
      msg.className = 'vhp-dm__msg';
      const words = document.createElement('span');
      words.textContent = line;
      msg.append(words);
      if (n === 0) {
        const dots = document.createElement('span');
        dots.className = 'vhp-dm__dots';
        dots.setAttribute('aria-hidden', 'true');
        dots.append(document.createElement('i'), document.createElement('i'), document.createElement('i'));
        msg.append(dots);
      }
      col.append(msg);
    });
    them.append(col);
    el.replaceChildren(them);
    el.hidden = !list.length;
  };

  const readSeen = () => {
    try {
      return JSON.parse(localStorage.getItem(SEEN_KEY) || '{}') || {};
    } catch (e) {
      return {};
    }
  };
  const seen = (key) => Boolean(readSeen()[key]);
  const markSeen = (key) => {
    const all = readSeen();
    all[key] = Date.now();
    // Keep the 60 newest flags.
    const keep = Object.entries(all).sort((a, b) => b[1] - a[1]).slice(0, 60);
    try {
      localStorage.setItem(SEEN_KEY, JSON.stringify(Object.fromEntries(keep)));
    } catch (e) {
      /* private mode: shows again */
    }
  };

  window.vhpUi = { motionOk, buzz, morph, growIn, burst, dm, seen, markSeen };
})();
